import { formatNumber } from '@angular/common';
import { Component, Inject, Input, LOCALE_ID, OnInit } from '@angular/core';
import { CostTypeHelper } from '@core/helpers/cost-type.helper';
import { BudgetTypeEnum } from '@enums/BudgetType';
import { Estimate } from '@interfaces/Estimate';
import { EstimateLine } from '@interfaces/EstimateLine';
import { GroupCostTypeDto } from '@interfaces/GroupCostTypeDto';
import { TreeNodeTotals } from '@models/TreeNodeTotals';
import { TranslateService } from '@ngx-translate/core';
import { TreeNode } from 'primeng/api';
import { TTEditableColumn, TreeTable, TreeTableEditEvent } from 'primeng/treetable';
import { PTreeNodeCellEditEvent } from '../models/p-edit-cell.event';
import { TreeNodeData, TreeNodeType } from '../models/tree-node-data.model';

@Component({
  selector: 'pop-expense-cell',
  templateUrl: './expense-cell.component.html',
  styleUrls: ['./expense-cell.component.less']
})
export class ExpenseCellComponent implements OnInit {
  @Input() public rowNode: {
    level?: number;
    visible?: boolean;
    node: TreeNode<TreeNodeData<EstimateLine>>;
    parent: TreeNode<TreeNodeData<EstimateLine>>;
  };
  @Input() public rowData: TreeNodeData<EstimateLine>;
  @Input() public groupCostTypes: GroupCostTypeDto[];
  @Input() public lowestExpenseLevel: TreeNodeType;
  @Input() public currentEstimate: Estimate;
  @Input() public field: keyof TreeNodeTotals;

  public isReadOnly: boolean;
  public tooltip: string;
  private isTimeOrInternalFeeCategory: boolean;
  private isLowestLevel: boolean;
  public isCellDisabled: boolean;

  public get isEditable() {
    if (this.isReadOnly) {
      return false;
    }
    return this.tt?.editingCell === this.editableColumn.el?.nativeElement;
  }

  public constructor(
    private tt: TreeTable,
    private editableColumn: TTEditableColumn,
    private translate: TranslateService,
    @Inject(LOCALE_ID) private locale: string
  ) {}

  public ngOnInit(): void {
    // expense categories -> cost type 3-8
    this.isTimeOrInternalFeeCategory =
      this.rowData.type === TreeNodeType.Category && !CostTypeHelper.isExpenseCategory(this.rowData.entity.category);
    this.isLowestLevel = this.rowData.type === this.lowestExpenseLevel;
    this.isReadOnly = this.expReadOnlyRules(this.rowData);
    this.isCellDisabled = this.isReadOnly && this.shouldDisableCell(this.rowData);
    this.tooltip = this.getExpensesTooltips();
  }

  public onBlur() {
    const event: PTreeNodeCellEditEvent = {
      data: this.field,
      field: this.rowNode
    };
    this.tt.onEditComplete.emit(event as unknown as TreeTableEditEvent); // trigger on edit
  }

  public expReadOnlyRules(rowData: TreeNodeData<EstimateLine>) {
    if ((this.isLowestLevel && this.isTimeOrInternalFeeCategory) || !this.isLowestLevel) {
      return true;
    } else if (this.groupCostTypes && this.groupCostTypes.length > 0) {
      return !this.groupHasExpense(rowData);
    }
    return false;
  }

  private groupHasExpense(rowData: TreeNodeData<EstimateLine>) {
    if (!this.isLowestLevel) {
      return false;
    }

    const estimateLine = rowData.entity;

    if (!estimateLine.structure?.categoryGroup) {
      return true;
    }
    const groupCode = estimateLine.structure?.categoryGroup?.code;
    const result = this.groupCostTypes.find(g => g.categoryGroupCode === groupCode);

    return result?.hasExpense;
  }

  private getExpensesTooltips() {
    if (!this.isCellDisabled) {
      return '';
    }

    if (this.isTimeOrInternalFeeCategory) {
      return this.translate.instant('TOOLTIP.COST_TYPE') as string;
    } else if (!this.groupHasExpense(this.rowData)) {
      return this.translate.instant('TOOLTIP.CATEGORY_GROUP') as string;
    }
    return '';
  }

  public userInput(value: string) {
    const totals = this.rowData.totals;

    if (this.field === 'expenseCostUnitCost' || this.field === 'expenseCostQuantity') {
      totals.updateTotalExpensesCost();
    } else {
      totals.setTotalExpenseCost(+value);
    }
  }

  private shouldDisableCell(rowData: TreeNodeData) {
    const isDetailBudgetType = this.currentEstimate.budgetType === BudgetTypeEnum.Detailed;
    const isSummaryBudgetTypeTime = this.currentEstimate.budgetTypeTime === BudgetTypeEnum.Summary;

    if (isDetailBudgetType && isSummaryBudgetTypeTime && rowData.type === TreeNodeType.Category) {
      return true;
    } else if (
      rowData.type === TreeNodeType.GenericTime ||
      rowData.type === TreeNodeType.GenericExpense ||
      rowData.type === TreeNodeType.GenericInternalFee ||
      this.isLowestLevel
    ) {
      return true;
    }
    return false;
  }

  public getValue() {
    const value = this.rowData.totals[this.field];
    const pattern = this.field === 'expenseCostQuantity' ? '1.0-2' : '1.2-2';

    if (this.isLowestLevel && value === undefined) {
      return '-';
    }

    if (typeof value !== 'number') return value;

    return value === undefined ? undefined : formatNumber(+value, this.locale, pattern);
  }
}
